import { useDebounceFn } from '@vueuse/core'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import { useRoute, useRouter } from '#app/composables/router'
import type useRootCategories from '~/composables/useRootCategories'
import { useCurrentShop } from '#storefront/composables'
import { PAGE_META_BUS } from '~/constants/general'
import { getAllCategoryPaths } from '~/utils/category'
import { businessUnits, getPageCategoryForTracking } from '~/utils/tracking'

/**
 * Tracks virtual page views with debouncing to prevent duplicate tracking during navigation.
 * Coordinates with SEO meta updates to ensure page_title is accurate before tracking.
 */
export default (rootCategories: ReturnType<typeof useRootCategories>['data']) => {
  const { trackVirtualPageView } = useTrackingEvents()
  const router = useRouter()
  const route = useRoute()
  const { locale } = useCurrentShop().value
  const metaBus = useEventBus<string>(PAGE_META_BUS)

  // Delay after route change to allow meta updates to complete before tracking
  const INITIAL_VIEW_TIMEOUT = 1500
  // Debounce rapid tracking calls to prevent duplicates
  const DEBOUNCE_TIME = 1000

  let lastTrackedRoute = ''
  let initialViewTimeout: ReturnType<typeof setTimeout> | null

  const clearInitialViewTimeout = () => {
    if (initialViewTimeout) {
      clearTimeout(initialViewTimeout)
      initialViewTimeout = null
    }
  }

  const track = (pageReferrer?: string) => {
    trackVirtualPageView({
      page_category: getPageCategoryForTracking(
        route,
        getAllCategoryPaths(rootCategories.value?.categories ?? []),
      ),
      page_title: document.title,
      page_path: document.location.pathname,
      page_referrer: pageReferrer ?? document.location.origin,
      // traffic_type?: 'internal' | 'external'
      site: locale,
      business_unit: businessUnits[locale],
    })
  }

  const trackDebounced = useDebounceFn(() => {
    if (lastTrackedRoute === route.fullPath) return

    const pageReferer = lastTrackedRoute ? document.referrer : undefined
    lastTrackedRoute = route.fullPath

    track(pageReferer)
  }, DEBOUNCE_TIME)

  onMounted(async () => {
    lastTrackedRoute = route.fullPath
    track(document.referrer)
  })

  router.afterEach(() => {
    clearInitialViewTimeout()
    initialViewTimeout = setTimeout(trackDebounced, INITIAL_VIEW_TIMEOUT)
  })

  // Cancel timeout on meta update start, track immediately on finish
  metaBus.on((event) => {
    if (event === 'seo-meta:started') {
      clearInitialViewTimeout()
      return
    }
    // else it's seo-meta:finished
    trackDebounced()
  })
}
